import React__default, { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { debounce } from 'throttle-debounce';
import { Button } from '../button/Button.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Box } from '../layout/Box.js';
import { Inline } from '../layout/Inline.js';
import { getColor } from '../styles/stylePropTypes.js';
import { TextInput } from './TextInput.js';

const StyledTextInput = styled(TextInput)`
  [data-hpy-web-text-input-has-icon] > &&[data-hpy-web-text-input-input] {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  &[data-hpy-web-text-input-input] {
    border: none;
    background-color: transparent;

    &::placeholder {
      color: ${COLOR_PALETTE.slateGreyDark50};
    }
  }

  & ~ [data-hpy-web-text-input-input-inner-label] {
    opacity: 0;
  }

  & ~ [data-hpy-web-text-input-input-icon] {
    color: ${COLOR_PALETTE.slateGreyDark50};
    margin-top: 0.375rem;
    height: 1.5rem;
    width: 1.5rem;
    font-size: 1.5rem;
  }
`;
const StyledBox = styled(Box)`
  &:focus,
  &:focus-within,
  &:hover {
    border-color: ${(props)=>getColor("context", props.theme)};
  }

  & [${Button.dataId}] {
    padding: 1rem;
    min-width: 0;
    font-weight: 400;
  }
`;
/**
 * 'Search' styled Text Input with Formik Field bindings.
 * To set current placeholder, use the `label` prop (matches functionality with standard `TextField`)
 *
 * @example
 * <SearchInput name="search" label="Search Properties" />
 */ const SearchInput = forwardRef(({ label ="Search" , testId ="X" , throttleMS =1500 , children , ...props }, ref)=>{
    const [value, setValue] = useState(props.value ?? "");
    useEffect(()=>{
        setValue(props.value ?? "");
    }, [
        props.value
    ]);
    // Debounce search change callback so that we don't attempt to search data until the search term is 'stable'.
    const throttleOnChange = useRef(debounce(throttleMS, false, (value)=>props.onChange?.(value)));
    const onChange = useCallback((value)=>{
        setValue(value);
        props.onChange && throttleOnChange.current(value);
    }, [
        props.onChange,
        throttleOnChange
    ]);
    const onBlur = useCallback((value)=>{
        setValue(value);
        props?.onBlur && props?.onBlur(value);
    }, [
        props
    ]);
    const onFocus = useCallback((value)=>props?.onFocus && props?.onFocus(value), [
        props
    ]);
    return React__default.createElement(StyledBox, {
        width: "fill",
        border: "solid",
        borderColor: "slateGrey20",
        borderRadius: "large",
        padding: [
            0,
            0.5,
            0,
            0
        ]
    }, React__default.createElement(Inline, {
        wrap: false,
        width: "fill"
    }, React__default.createElement(Box, {
        flexGrow: 1
    }, React__default.createElement(StyledTextInput, {
        "data-hpy-web-search-input": true,
        ...props,
        ref: ref,
        testId: "SearchInput-" + testId,
        icon: "search",
        label: label,
        value: value,
        onChange: onChange,
        onBlur: onBlur,
        onFocus: onFocus
    })), React__default.createElement(Inline, {
        wrap: false,
        alignY: "center",
        gap: 0.25
    }, children)));
});
SearchInput.displayName = "SearchInput";
SearchInput.dataId = "data-hpy-web-search-input";

export { SearchInput };
